import {
  ErrorComponent,
  Link,
  rootRouteId,
  useMatch,
  useRouter,
} from '@tanstack/react-router'
import { useClerk } from '@clerk/tanstack-react-start'
import { Box, Button, Group, Stack } from '@mantine/core'
import { useEffect } from 'react'
import type { ErrorComponentProps } from '@tanstack/react-router'
import {
  POSTHOG_ERROR_PROPERTIES,
  captureException,
  usePostHog,
} from '~/lib/posthog'

export function DefaultCatchBoundary({ error }: ErrorComponentProps) {
  const router = useRouter()
  const posthog = usePostHog()
  const match = useMatch({ strict: false, select: (s) => s })
  const routeId = match.id
  const isRoot = routeId === rootRouteId
  const clerk = useClerk()

  // Capture to PostHog Error Tracking (production only)
  useEffect(() => {
    // posthog may be undefined during deferred loading or if blocked by adblocker
    if (posthog) {
      captureException(posthog, error, {
        [POSTHOG_ERROR_PROPERTIES.ROUTE_ID]: routeId,
      })
    }
  }, [posthog, error, routeId])

  console.error(error)

  const handleSignOut = () => {
    try {
      clerk.signOut({ redirectUrl: '/' })
    } catch {
      window.location.href = '/sign-out'
    }
  }

  const buttonProps = {
    variant: 'filled' as const,
    color: 'gray',
    size: 'sm',
    style: { textTransform: 'uppercase' as const, fontWeight: 700 },
  }

  return (
    <Box
      p="md"
      style={{
        minWidth: 0,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Stack gap="lg" align="center">
        <ErrorComponent error={error} />
        <Group gap="xs" wrap="wrap" justify="center">
          <Button
            {...buttonProps}
            onClick={() => {
              router.invalidate()
            }}
          >
            Try Again
          </Button>
          {isRoot ? (
            <Button
              component={Link}
              to="/"
              {...buttonProps}
            >
              Home
            </Button>
          ) : (
            <Button
              component={Link}
              to="/"
              {...buttonProps}
              onClick={(e) => {
                e.preventDefault()
                window.history.back()
              }}
            >
              Go Back
            </Button>
          )}
          <Button {...buttonProps} onClick={handleSignOut}>
            Sign out
          </Button>
          <Button component={Link} to="/sign-out" {...buttonProps}>
            Sign out (if button fails)
          </Button>
        </Group>
      </Stack>
    </Box>
  )
}
